/**
 * Service for managing schedule data via the server API.
 * Schedule data is stored in SQLite with per-section versioning.
 */

const API_BASE_URL = '/api';

const getAuthToken = () => localStorage.getItem('authToken');

const apiRequest = async (endpoint, options = {}) => {
  const token = getAuthToken();
  const headers = {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
    'Expires': '0',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers,
  };

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method: options.method || 'GET',
    headers,
    body: options.body,
    cache: 'no-store',
  });

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = { error: `HTTP error! status: ${response.status}` };
    }

    if (response.status === 409 && errorData.conflict) {
      const err = new Error('Schedule conflict detected');
      err.conflict = true;
      err.details = errorData;
      err.status = 409;
      throw err;
    }

    throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
  }

  const text = await response.text();
  return JSON.parse(text);
};

// ---------------------------------------------------------------------------
// Schedule CRUD
// ---------------------------------------------------------------------------

/**
 * Fetch the current schedule JSON (same shape as before).
 */
export const getCurrentSchedule = async () => {
  const response = await fetch('/schedule.json', { cache: 'no-store' });
  if (!response.ok) throw new Error('Failed to fetch schedule');
  return response.json();
};

/**
 * Fetch current version numbers for all sections.
 */
export const getScheduleVersions = async () => {
  return apiRequest('/schedule/versions');
};

/**
 * Save the full schedule with optimistic locking.
 * @param {Object} scheduleData - The full schedule object
 * @param {Object} versions - Version map: { weeklySchedules: 3, ... }
 * @returns {{ success: boolean, versions: Object }}
 */
export const saveSchedule = async (scheduleData, versions) => {
  if (!getAuthToken()) throw new Error('Authentication required');
  return apiRequest('/schedule', {
    method: 'POST',
    body: JSON.stringify({ data: scheduleData, versions }),
  });
};

/**
 * Update a single section with optimistic locking.
 * @param {string} section - Section key (e.g. 'displayOptions')
 * @param {*} data - The section data
 * @param {number} version - The version the client loaded
 * @returns {{ success: boolean, version: number }}
 */
export const patchSection = async (section, data, version) => {
  if (!getAuthToken()) throw new Error('Authentication required');
  return apiRequest(`/schedule/${section}`, {
    method: 'PATCH',
    body: JSON.stringify({ data, version }),
  });
};

// ---------------------------------------------------------------------------
// History / Backups
// ---------------------------------------------------------------------------

/**
 * List recent schedule history entries.
 */
export const getScheduleFiles = async () => {
  if (!getAuthToken()) throw new Error('Authentication required');
  const result = await apiRequest('/schedule/history');
  return result.history || [];
};

/**
 * Get a specific history entry's data.
 */
export const getFileContent = async (id) => {
  if (!getAuthToken()) throw new Error('Authentication required');
  return apiRequest(`/schedule/history/${id}`);
};

/**
 * Restore a history entry as the current section data.
 */
export const restoreBackup = async (id) => {
  if (!getAuthToken()) throw new Error('Authentication required');
  return apiRequest(`/schedule/restore/${id}`, { method: 'POST' });
};

/**
 * Delete a history entry.
 */
export const deleteBackup = async (id) => {
  if (!getAuthToken()) throw new Error('Authentication required');
  return apiRequest(`/schedule/history/${id}`, { method: 'DELETE' });
};

// ---------------------------------------------------------------------------
// Default weekly schedules (DB-backed)
// ---------------------------------------------------------------------------

export const saveWeeklySchedulesAsDefault = async (weeklySchedules) => {
  if (!getAuthToken()) throw new Error('Authentication required');
  return apiRequest('/schedule/defaults', {
    method: 'POST',
    body: JSON.stringify({ weeklySchedules }),
  });
};

export const getDefaultWeeklySchedules = async () => {
  if (!getAuthToken()) throw new Error('Authentication required');
  const result = await apiRequest('/schedule/defaults');
  if (!result.defaults) return null;
  return { weeklySchedules: result.defaults, savedAt: result.savedAt };
};

export const restoreDefaultWeeklySchedules = async (currentVersions) => {
  if (!getAuthToken()) throw new Error('Authentication required');

  const defaultData = await getDefaultWeeklySchedules();
  if (!defaultData) throw new Error('No default weekly schedules found');

  const currentSchedule = await getCurrentSchedule();
  const updatedSchedule = {
    ...currentSchedule,
    weeklySchedules: defaultData.weeklySchedules,
  };

  const result = await saveSchedule(updatedSchedule, currentVersions);
  return {
    success: true,
    message: 'Default weekly schedules restored successfully',
    schedule: updatedSchedule,
    versions: result.versions,
    defaultSavedAt: defaultData.savedAt,
  };
};

// ---------------------------------------------------------------------------
// Print configuration (unchanged)
// ---------------------------------------------------------------------------

export const savePrintConfig = async (printConfig) => {
  const response = await apiRequest('/print-config', {
    method: 'POST',
    body: JSON.stringify(printConfig),
  });
  return { success: true, message: 'Print configuration saved successfully', ...response };
};

export const loadPrintConfig = async () => {
  try {
    return await apiRequest(`/print-config?t=${Date.now()}`);
  } catch {
    return { selectedSections: [], weeklySections: [], datedSections: [] };
  }
};

export const saveNamedPrintConfig = async (configName, config) => {
  const sanitizedName = configName.replace(/[^a-zA-Z0-9_-]/g, '_');
  return apiRequest('/print-configs', {
    method: 'POST',
    body: JSON.stringify({ name: sanitizedName, config }),
  });
};

export const loadNamedPrintConfig = async (configName) => {
  const response = await apiRequest(`/print-configs/${configName}`);
  return response.config;
};

export const listNamedPrintConfigs = async () => {
  const response = await apiRequest('/print-configs');
  return response.configs || [];
};

export const deleteNamedPrintConfig = async (configName) => {
  return apiRequest(`/print-configs/${configName}`, { method: 'DELETE' });
};
